import { Redirect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { ActivityIndicator, Image, StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { colors, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { openAppOrStore } from '@/lib/openAppOrStore';
import { authStore } from '@/stores/auth.store';

// The home screen inside a drawn iPhone frame (transparent corners baked
// into the PNG). Explicit px dims (asset is 760x1443, ratio preserved):
// RN-web needs both — percentage width + aspectRatio left the height
// unresolved and the image rendered zoomed/cropped.
const appPreview = require('@/assets/images/gifting/app-preview.png');
const PREVIEW_WIDTH = 270;
const PREVIEW_HEIGHT = 513;

/**
 * The iOS-web terminal: profile is complete, and the claim itself happens in
 * the app (Jason's call, 8/6 — "if we hide the gifts behind the app
 * download, they'll do whatever they need to do to get the gift"). By the
 * time anyone lands here their account already exists with a full profile,
 * so the App Store round trip losing the link costs NOTHING: they open the
 * app, sign in with the phone they just verified, and the server routes
 * them straight to the grid.
 *
 * ONE button, two outcomes (Jeff, 2026-08-07): openAppOrStore tries the
 * app's custom scheme and falls back to the App Store — installed users are
 * never bounced through the store. The screenshot below the button shows
 * what's waiting on the other side of the download.
 *
 * Same render rule as every campaign screen: we render only when the server
 * (via giftingRouteFor, which owns the iOS-web branch) routes here.
 */
const GetTheAppScreen = observer(() => {
  if (!authStore.hydrated) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={colors.deepGreen} />
      </View>
    );
  }

  const step = giftingRouteFor(authStore.user);
  if (step !== GIFTING_ROUTES.getTheApp) {
    return <Redirect href={step ?? GIFTING_ROUTES.landing} />;
  }

  return (
    <Screen width="column">
      <View style={styles.body}>
        <Overline>{campaignCopy(authStore.user?.gifting_copy).name}</Overline>
        <Heading size="4xl" style={styles.headline}>
          You&apos;re in.
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          Gifts are claimed in the MarketPryce Rewards app. Open it, sign in with your phone
          number, and pick the one you want.
        </Text>

        <Button label="Open the app" onPress={() => openAppOrStore()} style={styles.cta} />
        <Text variant="caption" color="textMuted" align="center" style={styles.note}>
          Don&apos;t have it yet? The same button takes you to the App Store.
        </Text>

        <Image
          source={appPreview}
          style={styles.preview}
          resizeMode="contain"
          accessibilityLabel="The MarketPryce Rewards app home screen on an iPhone"
        />
      </View>
    </Screen>
  );
});

export default GetTheAppScreen;

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  body: {
    paddingTop: spacing.sm,
  },
  headline: {
    marginTop: spacing.md,
  },
  copy: {
    marginTop: spacing.base,
  },
  cta: {
    marginTop: spacing.lg,
  },
  note: {
    marginTop: spacing.sm,
  },
  preview: {
    alignSelf: 'center',
    width: PREVIEW_WIDTH,
    height: PREVIEW_HEIGHT,
    marginTop: spacing.lg,
  },
});
